import { CHAIN_PROFILES, isEnvironment, type Environment } from "./chains.ts";
import type { Deployment } from "./deployment.ts";
import { isOnboardingState, type OnboardingState } from "./onboarding.ts";
import { RP_IDS } from "./relying-party.ts";

/**
 * What the SPA is told about the deployment it was served from (#158), both
 * ends of the wire: the gate encodes it with `deploymentFactsOf`, and the SPA
 * decodes it with `parseDeploymentFacts`. One file, so one test can pin that
 * they agree (#183).
 *
 * Its own file, with no zod, for the reason `mounts.ts` gives. `Deployment`
 * is imported as a type only, so nothing of `deployment.ts` reaches the page.
 */

/**
 * The SPA is one bundle, baked into the Caddy image and byte-for-byte the
 * same on staging and production, so these come from the gate at startup
 * rather than from the build. That is what lets the bundle staging ran be the
 * one production serves.
 *
 * Public, so nothing here may be a secret, an operational number or a way in:
 * no CDP URL, no caps, no product wallet. Four fields, and a gate test pins
 * exactly these four so that a fifth has to be argued for.
 */
export type DeploymentFacts = {
  readonly environment: Environment;
  readonly chainId: number;
  readonly rpId: string;
  readonly onboarding: OnboardingState;
};

export class DeploymentFactsError extends Error {
  override name = "DeploymentFactsError";
}

/**
 * Built field by field, never by spreading or filtering the deployment
 * (CODING_STANDARDS.md, "allowlist, never denylist"): a field added to
 * `Deployment` later stays private until somebody adds it here by name.
 */
export function deploymentFactsOf(deployment: Deployment): DeploymentFacts {
  return {
    environment: deployment.environment,
    chainId: deployment.chain.id,
    rpId: deployment.rpId,
    onboarding: deployment.onboarding,
  };
}

/**
 * Check a decoded answer from the gate's `/deployment`, or throw
 * `DeploymentFactsError`.
 *
 * Built field by field from the four names `deploymentFactsOf` serves, so
 * anything else in the answer is dropped rather than carried into the app.
 * And it fails closed: an answer the SPA cannot fully trust renders no
 * onboarding, because the RP ID in it is permanent for every credential
 * registered under it (ADR-0010 decision 2).
 *
 * The chain id and the RP ID are checked against the committed tables for the
 * environment named, rather than taken on their own. Each is one fact stated
 * twice, and a deployment that disagrees with itself is not one to sign
 * anything on.
 */
export function parseDeploymentFacts(body: unknown): DeploymentFacts {
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    throw new DeploymentFactsError("the deployment facts are not an object");
  }
  const { environment, chainId, rpId, onboarding } = body as Record<
    string,
    unknown
  >;

  if (!isEnvironment(environment)) {
    throw new DeploymentFactsError("environment is not a known environment");
  }
  if (chainId !== CHAIN_PROFILES[environment].chainId) {
    throw new DeploymentFactsError(
      `chainId is not ${environment}'s committed chain`,
    );
  }
  if (rpId !== RP_IDS[environment]) {
    throw new DeploymentFactsError(
      `rpId is not ${environment}'s committed RP ID`,
    );
  }
  if (!isOnboardingState(onboarding)) {
    throw new DeploymentFactsError("onboarding is not open or closed");
  }

  return { environment, chainId, rpId, onboarding };
}
